/**
 * Single source of truth for parent/guardian relationship values used across
 * the app. Handles:
 *
 *   • The canonical option list rendered in Create/Edit Student dropdowns.
 *   • Legacy-value normalisation so historical rows ("Parent", "guardian",
 *     "Other") map cleanly onto the new snake_case set.
 *   • Display-label lookup so UIs never hardcode "Guardian" — the label a
 *     user selected is the label every screen shows.
 *
 * Never duplicate a switch statement on `relationship_type` in a component.
 * Use `getRelationshipLabel(value, other?)` from here.
 */

export type RelationshipValue =
  | "mother"
  | "father"
  | "parent"
  | "legal_guardian"
  | "grandmother"
  | "grandfather"
  | "aunt"
  | "uncle"
  | "sibling"
  | "foster_parent"
  | "other";

export interface RelationshipOption {
  value: RelationshipValue;
  label: string;
}

/** Ordered option list rendered in dropdowns/chip pickers. */
export const RELATIONSHIP_OPTIONS: readonly RelationshipOption[] = Object.freeze([
  { value: "mother", label: "Mother" },
  { value: "father", label: "Father" },
  { value: "parent", label: "Parent" },
  { value: "legal_guardian", label: "Legal Guardian" },
  { value: "grandmother", label: "Grandmother" },
  { value: "grandfather", label: "Grandfather" },
  { value: "aunt", label: "Aunt" },
  { value: "uncle", label: "Uncle" },
  { value: "sibling", label: "Sibling" },
  { value: "foster_parent", label: "Foster Parent" },
  { value: "other", label: "Other" },
]);

const CANONICAL_SET = new Set<string>(RELATIONSHIP_OPTIONS.map((o) => o.value));

// Legacy → canonical rewrites. Only cover values we actually shipped with;
// anything else is preserved as-is so bespoke historical strings don't get
// silently lost.
const LEGACY_MAP: Record<string, RelationshipValue> = {
  parent: "parent",
  guardian: "legal_guardian",
  other: "other",
};

/**
 * Normalise any stored relationship value to its canonical snake_case form.
 * Case-insensitive; whitespace-tolerant. Unknown non-empty values are
 * lowercased and returned as-is (preserved for the "unknown legacy string"
 * case) so we never drop information.
 */
export const normalizeRelationshipValue = (raw?: string | null): RelationshipValue | string | null => {
  if (raw == null) return null;
  const cleaned = String(raw).trim();
  if (!cleaned) return null;
  const lower = cleaned.toLowerCase().replace(/\s+/g, "_");
  if (CANONICAL_SET.has(lower)) return lower as RelationshipValue;
  if (LEGACY_MAP[lower]) return LEGACY_MAP[lower];
  return lower;
};

/**
 * Human-readable label for a stored relationship value.
 *
 * When `value === "other"` and `relationshipOther` is supplied, that free-text
 * value wins so the UI shows "Case Worker" instead of a generic "Other".
 *
 * Missing/unknown values fall back to "Parent / Guardian" — matches the
 * section-title convention when we don't know the exact relationship.
 */
export const getRelationshipLabel = (raw?: string | null, relationshipOther?: string | null): string => {
  const normalised = normalizeRelationshipValue(raw);
  if (normalised === "other") {
    const custom = (relationshipOther ?? "").trim();
    return custom || "Other";
  }
  if (normalised && CANONICAL_SET.has(normalised as string)) {
    const opt = RELATIONSHIP_OPTIONS.find((o) => o.value === normalised);
    if (opt) return opt.label;
  }
  if (normalised && typeof normalised === "string" && normalised.length > 0) {
    // Unknown legacy string — title-case it so it's at least presentable.
    return normalised
      .split("_")
      .map((w) => (w ? w.charAt(0).toUpperCase() + w.slice(1) : w))
      .join(" ");
  }
  return "Parent / Guardian";
};
